import { 
  QueryTypeEnum as QueryType,
  SQLDialect,
  TableRef,
  AggregateOperationEnum,
  Query,
  SelectQueryOptions,
  AggregateQueryOptions,
  SQLQueryOptions
} from '@/lib/engine';
import { ColumnRef } from '../data-grid/types';

/**
 * Interface for buildDefaultQuery function parameters with simplified types
 */
export interface BuildDefaultQueryParams {
  /** The query type to build */
  queryType: QueryType;
  /** The table to build the query for - only path is used */
  table: { path: string };
  /** Available columns for the query */
  availableColumns?: ColumnRef[];
}

/**
 * Build a default query for the specified query type based on available columns
 * 
 * This is a pure function that creates a query object based on the provided parameters.
 */
export function buildDefaultQuery({ queryType, table, availableColumns = [] }: BuildDefaultQueryParams): Query {
  // Get path with a fallback in case it's undefined
  const tablePath = table?.path || 'unknown_table';
  
  // Create appropriate query based on type
  switch (queryType) {
    case QueryType.SELECT: {
      // If no columns are available, create a selection with a wildcard column
      if (availableColumns.length === 0) {
        // Create a wildcard column reference
        const wildcardColumn: ColumnRef = {
          name: '*',
          schema: {
            name: '*',
            type: 'varchar',
            nullable: true
          }
        };
        
        const options: SelectQueryOptions = {
          columns: [wildcardColumn],
          where: "",
          orderBy: []
        };
        return {
          type: QueryType.SELECT,
          options
        };
      }
      
      // If columns are available, create a SELECT query with them
      const options: SelectQueryOptions = {
        columns: availableColumns,
        where: "",
        orderBy: []
      };
      return {
        type: QueryType.SELECT,
        options
      };
    }
      
    case QueryType.AGGREGATE: {
      // If no columns available, create a simple SELECT query with wildcard
      if (availableColumns.length === 0) {
        // Create a wildcard column reference
        const wildcardColumn: ColumnRef = {
          name: '*',
          schema: {
            name: '*',
            type: 'varchar',
            nullable: true
          }
        };
        
        const options: SelectQueryOptions = {
          columns: [wildcardColumn],
          where: "",
          orderBy: []
        };
        
        return {
          type: QueryType.SELECT,
          options
        };
      }

      // We know availableColumns has at least one element here
      const firstColumn = availableColumns[0] as ColumnRef;
      const options: AggregateQueryOptions = {
        groupBy: [firstColumn],
        aggregates: [{
          operation: AggregateOperationEnum.COUNT,
          column: firstColumn,
          alias: `count_${firstColumn.name || 'col'}`
        }],
        where: "",
        having: "",
        orderBy: []
      };

      return {
        type: QueryType.AGGREGATE,
        options
      };
    }
      
    case QueryType.SQL:
    default: {
      // Convert SQL type to SELECT with wildcard column
      const wildcardColumn: ColumnRef = {
        name: '*',
        schema: {
          name: '*',
          type: 'varchar',
          nullable: true
        }
      };
      
      const options: SelectQueryOptions = {
        columns: [wildcardColumn],
        where: "",
        orderBy: []
      };
      
      return {
        type: QueryType.SELECT,
        options
      };
    }
  }
} 